<template>
  <v-main>
    <v-container fluid>
      <!-- Combined Header and Filtration Section -->
      <v-row class="mb-2">
        <v-col cols="12">
          <v-card elevation="3">
            <!-- Page Header -->
            <page-header
              :title="isDeletePage() != true ? 'Tip of the Day Management' : 'Deleted Daily Tips'"
              :subtitle="isDeletePage() != true ? 'Manage the health tip shown to patients each day' : 'Restore or permanently delete daily tips'"
            >
              <template v-slot:actions>
                <div v-if="isDeletePage() != true" class="d-flex justify-end align-center">
                    <v-btn
                      color="secondary"
                      outlined
                      elevation="0"
                      small
                      @click="openBulkUploadDialog"
                      class="mr-2 px-3"
                    >
                      <v-icon left x-small>mdi-file-excel</v-icon>
                      Bulk Upload
                    </v-btn>
                    <v-btn
                      color="primary"
                      elevation="1"
                      small
                      to="/daily-tips/create"
                      class="mr-2 px-3"
                    >
                      <v-icon left x-small>mdi-plus</v-icon>
                      Add Daily Tip
                    </v-btn>
                  </div>
              </template>
            </page-header>

            <!-- Filtration Section -->
            <v-card-subtitle class="pb-1 pt-2 px-4">
              <v-row class="w-100 ma-0" align="center">
                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex align-center">
                    <v-icon class="mr-2" color="primary" size="20"
                      >mdi-filter-variant</v-icon
                    >
                    <span class="text-body-2 font-weight-medium"
                      >Filter Daily Tips</span
                    >
                  </div>
                </v-col>
                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <v-chip
                      v-if="hasActiveFilters"
                      color="info"
                      x-small
                      class="mr-2"
                    >
                      <v-icon left x-small>mdi-filter</v-icon>
                      {{ getActiveFiltersCount() }} Active
                    </v-chip>
                    <v-btn
                      text
                      x-small
                      color="error"
                      @click="clearAllFilters"
                      :disabled="!hasActiveFilters"
                    >
                      <v-icon left x-small>mdi-filter-remove</v-icon>
                      Clear
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-subtitle>

            <v-card-text class="px-4 pb-4">
              <v-row>
                <!-- Search Field -->
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model="filter.search_keyword"
                    label="Search Daily Tips"
                    prepend-inner-icon="mdi-magnify"
                    outlined
                    dense
                    clearable
                    hide-details
                    @click:clear="
                      (filter.search_keyword = null), (page = 1), getData()
                    "
                    @keydown.enter="(page = 1), getData()"
                    placeholder="Search by title or content..."
                  ></v-text-field>
                </v-col>

                <!-- Status Filter -->
                <v-col cols="12" md="3">
                  <v-autocomplete
                    v-model="filter.is_active"
                    :items="statuses"
                    item-text="text"
                    item-value="value"
                    label="Status"
                    outlined
                    dense
                    clearable
                    hide-details
                    prepend-inner-icon="mdi-toggle-switch"
                    @input="(page = 1), getData()"
                  ></v-autocomplete>
                </v-col>

                <!-- Show Deleted Filter -->
                <v-col cols="12" md="3">
                  <v-autocomplete
                    v-model="filter.show_deleted"
                    :items="deletedStatuses"
                    item-text="text"
                    item-value="value"
                    label="View Type"
                    outlined
                    dense
                    clearable
                    hide-details
                    prepend-inner-icon="mdi-eye"
                    @input="(page = 1), getData()"
                  ></v-autocomplete>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Data Table Section -->
      <v-row>
        <v-col cols="12">
          <v-card elevation="3">
            <v-card-text class="px-0 py-0">
              <!-- Loading State -->
              <div v-if="isLoading" class="text-center py-12">
                <health-loader message="Loading daily tips..." />
              </div>

              <!-- Data Table -->
              <v-data-table
                v-else
                :headers="headers"
                :items="tips"
                :items-per-page="-1"
                hide-default-footer
                class="elevation-0"
                :class="{ 'empty-table': !tips.length }"
              >
                <template v-slot:[`item.sr_no`]="{ index }">
                  <span class="text-body-2">{{
                    (page - 1) * rowsPerPage + index + 1
                  }}</span>
                </template>

                <template v-slot:[`item.status`]="{ item }">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-chip
                        v-bind="attrs"
                        v-on="on"
                        small
                        :color="item.is_active ? 'success' : 'error'"
                        text-color="white"
                        class="font-weight-medium status-chip"
                      >
                        <v-icon left small>
                          {{
                            item.is_active
                              ? "mdi-check-circle"
                              : "mdi-close-circle"
                          }}
                        </v-icon>
                        {{ item.is_active ? "Active" : "Inactive" }}
                      </v-chip>
                    </template>
                    <span>{{
                      item.is_active ? "Tip is Active" : "Tip is Inactive"
                    }}</span>
                  </v-tooltip>
                </template>

                <template v-slot:[`item.tip_date`]="{ item }">
                  <v-chip
                    small
                    :color="isToday(item.tip_date) ? 'primary' : 'grey lighten-2'"
                    :text-color="isToday(item.tip_date) ? 'white' : 'black'"
                    class="font-weight-medium status-chip"
                  >
                    <v-icon left small>mdi-calendar</v-icon>
                    {{ formatDate(item.tip_date) }}
                  </v-chip>
                  <div v-if="isToday(item.tip_date)" class="text-caption primary--text mt-1">
                    Today's Tip
                  </div>
                </template>

                <template v-slot:[`item.title`]="{ item }">
                  <div
                    class="text-body-2 font-weight-medium text-truncate"
                    style="max-width: 420px"
                  >
                    {{ item.title }}
                  </div>
                  <div
                    v-if="item.summary"
                    class="text-caption grey--text text-truncate"
                    style="max-width: 420px"
                  >
                    {{ item.summary }}
                  </div>
                </template>

                <template v-slot:[`item.created_by`]="{ item }">
                  <span class="text-body-2">
                    {{ getCreatedByName(item.created_by) }}
                  </span>
                </template>

                <template v-slot:[`item.created_at`]="{ item }">
                  <span class="text-body-2">{{
                    formatDateTime(item.created_at)
                  }}</span>
                </template>

                <template v-slot:[`item.actions`]="{ item }">
                  <div class="d-flex justify-end align-center">
                    <template v-if="!isDeletePage()">
                      <v-tooltip top>
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn
                            v-bind="attrs"
                            v-on="on"
                            :to="`/daily-tips/${encryptIt(item.id)}`"
                            icon
                            small
                            color="primary"
                            class="mr-1 action-btn"
                          >
                            <v-icon small>mdi-pencil</v-icon>
                          </v-btn>
                        </template>
                        <span>Edit daily tip</span>
                      </v-tooltip>

                      <v-tooltip top>
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn
                            v-bind="attrs"
                            v-on="on"
                            @click="openDeleteDialog(item)"
                            icon
                            small
                            color="error"
                            class="action-btn"
                          >
                            <v-icon small>mdi-delete</v-icon>
                          </v-btn>
                        </template>
                        <span>Delete daily tip</span>
                      </v-tooltip>
                    </template>

                    <template v-else>
                      <v-tooltip top>
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn
                            v-bind="attrs"
                            v-on="on"
                            @click="Restore_this(item.id)"
                            icon
                            small
                            color="success"
                            class="action-btn"
                          >
                            <v-icon small>mdi-delete-restore</v-icon>
                          </v-btn>
                        </template>
                        <span>Restore daily tip</span>
                      </v-tooltip>
                    </template>
                  </div>
                </template>

                <template v-slot:no-data>
                  <empty-state icon="mdi-lightbulb-on-outline" :title="isDeletePage() ? &quot;No deleted daily tips found&quot; : &quot;No daily tips available&quot;" :message="isDeletePage() ? &quot;All daily tips are currently active&quot; : &quot;Create your first daily tip to get started&quot;" />
                </template>
              </v-data-table>

              <!-- Pagination -->
              <div
                v-if="tips.length && paginationLength > 1"
                class="px-4 py-3"
              >
                <v-divider class="mb-3"></v-divider>
                <div class="d-flex align-center justify-space-between">
                  <div class="text-body-2 grey--text">
                    Showing {{ (page - 1) * rowsPerPage + 1 }} to
                    {{ Math.min(page * rowsPerPage, count) }} of
                    {{ count }} entries
                  </div>
                  <v-pagination
                    v-model="page"
                    :length="paginationLength"
                    :total-visible="7"
                    circle
                    color="primary"
                    @input="getData()"
                  ></v-pagination>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <DeleteConfirmationDialog
        v-model="deleteDialog"
        :item="itemToDelete"
        :loading="deleteLoading"
        @confirm="confirmDelete"
        @cancel="deleteDialog = false"
      />

      <!-- Restore Success Dialog Box -->
      <v-dialog v-model="is_restored_tip" width="600">
        <v-card rounded="xxl">
          <v-container>
            <v-row align="center">
              <v-col sm="3" align="center">
                <v-icon
                  large
                  style="color: green; font-size: 90px; width: 200px !important"
                >
                  mdi-delete-restore
                </v-icon>
              </v-col>
              <v-col sm="9" align="center">
                <v-card-text>
                  <v-row>
                    <v-col>
                      <h1 style="color: green">Hurray!</h1>
                      <br />
                      <h3>Daily tip restored successfully.</h3>
                      <br />
                      <v-btn
                        color="success"
                        @click="is_restored_tip = false"
                      >
                        OK
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-col>
            </v-row>
          </v-container>
        </v-card>
      </v-dialog>

      <!-- Bulk Upload Dialog -->
      <v-dialog v-model="bulkUploadDialog" max-width="800" persistent scrollable>
        <v-card>
          <v-card-title class="d-flex align-center justify-space-between">
            <span>Upload Daily Tips</span>
            <v-btn icon small @click="closeBulkUploadDialog" :disabled="isPreviewing || isUploading">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-divider></v-divider>

          <v-card-text class="pt-4" style="max-height: 70vh">
            <!-- ===================== STAGE 1: SELECT FILE ===================== -->
            <template v-if="uploadStage === 'select'">
              <div class="d-flex align-center justify-space-between mb-3">
                <span class="text-body-2 grey--text text--darken-1">
                  <strong>Tip Date</strong> and <strong>Title</strong> are required;
                  other columns are optional. Dates already uploaded are skipped.
                </span>
                <v-btn
                  text
                  small
                  color="primary"
                  :loading="isDownloadingTemplate"
                  @click="downloadTemplate"
                  class="flex-shrink-0 ml-2"
                >
                  <v-icon left small>mdi-download</v-icon>
                  Template
                </v-btn>
              </div>

              <div
                class="upload-dropzone"
                :class="{ 'upload-dropzone--active': isDragging }"
                @dragover.prevent="isDragging = true"
                @dragleave.prevent="isDragging = false"
                @drop.prevent="onFileDrop"
                @click="$refs.fileInput.click()"
              >
                <input
                  ref="fileInput"
                  type="file"
                  accept=".xlsx,.xls"
                  class="d-none"
                  @change="onFileSelected"
                />
                <v-icon size="40" color="grey darken-1">mdi-tray-arrow-up</v-icon>
                <p class="text-body-1 mt-2 mb-0">Drop your spreadsheet here</p>
                <p class="text-caption grey--text mb-0">or click to browse — .xlsx or .xls</p>
              </div>

              <v-alert v-if="bulkUploadFileError" dense text type="error" class="mt-3 mb-0">
                {{ bulkUploadFileError }}
              </v-alert>

              <div v-if="isPreviewing" class="text-center py-6">
                <v-progress-circular indeterminate color="primary" size="32"></v-progress-circular>
                <p class="text-body-2 grey--text mt-2 mb-0">Validating file...</p>
              </div>
            </template>

            <!-- ===================== STAGE 2: PREVIEW ===================== -->
            <template v-if="uploadStage === 'preview' && previewData">
              <div class="d-flex align-center justify-space-between file-chip mb-3">
                <div class="d-flex align-center">
                  <v-icon color="green darken-1" class="mr-2">mdi-file-excel</v-icon>
                  <div>
                    <div class="text-body-2 font-weight-medium">{{ previewData.filename }}</div>
                    <div class="text-caption grey--text">{{ formatFileSize(previewData.file_size) }}</div>
                  </div>
                </div>
                <div>
                  <v-btn text x-small color="primary" @click="downloadTemplate">Template</v-btn>
                  <v-btn text x-small color="error" @click="resetToSelectStage">Remove</v-btn>
                </div>
              </div>

              <v-alert dense text :type="previewAlertType(previewData.summary)" class="mb-3">
                {{ previewAlertText(previewData.summary) }}
              </v-alert>

              <v-row dense class="mb-3">
                <v-col cols="3">
                  <div class="stat-tile">
                    <div class="text-caption grey--text text-uppercase">Rows</div>
                    <div class="text-h6">{{ previewData.summary.total_rows }}</div>
                  </div>
                </v-col>
                <v-col cols="3">
                  <div class="stat-tile">
                    <div class="text-caption grey--text text-uppercase">Will Import</div>
                    <div class="text-h6 success--text">{{ previewData.summary.will_import }}</div>
                  </div>
                </v-col>
                <v-col cols="3">
                  <div class="stat-tile">
                    <div class="text-caption grey--text text-uppercase">Duplicate</div>
                    <div class="text-h6 warning--text text--darken-2">{{ previewData.summary.duplicate }}</div>
                  </div>
                </v-col>
                <v-col cols="3">
                  <div class="stat-tile">
                    <div class="text-caption grey--text text-uppercase">Failed</div>
                    <div class="text-h6" :class="previewData.summary.failed ? 'error--text' : ''">
                      {{ previewData.summary.failed }}
                    </div>
                  </div>
                </v-col>
              </v-row>

              <div class="text-overline grey--text mb-1">
                Preview — {{ previewData.rows.length }} rows
              </div>
              <v-data-table
                :headers="previewHeaders"
                :items="previewData.rows"
                item-key="row_number"
                dense
                :items-per-page="10"
                class="preview-table"
              >
                <template v-slot:[`item.result`]="{ item }">
                  <v-chip x-small :color="resultChipColor(item.result)" text-color="white">
                    {{ resultLabel(item.result) }}
                  </v-chip>
                </template>
                <template v-slot:[`item.reason`]="{ item }">
                  <span class="text-caption">{{ item.reason || "—" }}</span>
                </template>
              </v-data-table>
            </template>

            <!-- ===================== STAGE 3: RESULT ===================== -->
            <template v-if="uploadStage === 'result' && resultData">
              <v-alert dense text :type="previewAlertType(resultData.summary, true)" class="mb-1">
                <div class="font-weight-medium">{{ resultAlertText(resultData.summary) }}</div>
                <div class="text-caption">{{ resultData.filename }}</div>
              </v-alert>

              <v-row dense class="my-3">
                <v-col cols="3">
                  <div class="stat-tile">
                    <div class="text-caption grey--text text-uppercase">Rows</div>
                    <div class="text-h6">{{ resultData.summary.total_rows }}</div>
                  </div>
                </v-col>
                <v-col cols="3">
                  <div class="stat-tile">
                    <div class="text-caption grey--text text-uppercase">Imported</div>
                    <div class="text-h6 success--text">{{ resultData.summary.imported }}</div>
                  </div>
                </v-col>
                <v-col cols="3">
                  <div class="stat-tile">
                    <div class="text-caption grey--text text-uppercase">Duplicate</div>
                    <div class="text-h6 warning--text text--darken-2">{{ resultData.summary.duplicate }}</div>
                  </div>
                </v-col>
                <v-col cols="3">
                  <div class="stat-tile">
                    <div class="text-caption grey--text text-uppercase">Failed</div>
                    <div class="text-h6" :class="resultData.summary.failed ? 'error--text' : ''">
                      {{ resultData.summary.failed }}
                    </div>
                  </div>
                </v-col>
              </v-row>

              <template v-if="resultData.rejected.length">
                <div class="text-overline grey--text mb-1">What Was Rejected</div>
                <v-simple-table dense class="rejected-table">
                  <thead>
                    <tr>
                      <th>Row</th>
                      <th>Column</th>
                      <th>Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="row in resultData.rejected.slice(0, 6)" :key="row.row_number">
                      <td>{{ row.row_number }}</td>
                      <td>{{ row.column || "—" }}</td>
                      <td>{{ row.reason }}</td>
                    </tr>
                  </tbody>
                </v-simple-table>
                <p v-if="resultData.rejected.length > 6" class="text-caption grey--text mt-2">
                  {{ resultData.rejected.length - 6 }} more in the downloadable report.
                </p>
              </template>
            </template>
          </v-card-text>

          <v-divider></v-divider>

          <v-card-actions>
            <v-btn
              v-if="uploadStage === 'result' && resultData.rejected.length"
              text
              color="primary"
              :loading="isDownloadingErrors"
              @click="downloadErrorReport"
            >
              <v-icon left small>mdi-download</v-icon>
              Download Error Report
            </v-btn>
            <v-spacer></v-spacer>
            <template v-if="uploadStage !== 'result'">
              <v-btn text @click="closeBulkUploadDialog" :disabled="isPreviewing || isUploading">
                Cancel
              </v-btn>
              <v-btn
                color="primary"
                :loading="isUploading"
                :disabled="uploadStage !== 'preview' || !previewData || previewData.summary.will_import === 0"
                @click="confirmBulkUpload"
              >
                Upload
              </v-btn>
            </template>
            <v-btn v-else color="primary" text @click="closeBulkUploadDialog">
              Done
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </v-main>
</template>

<script>
import axios from "@/axios";
import DeleteConfirmationDialog from "@/components/DeleteConfirmationDialog.vue";

export default {
  name: "DailyTipListPage",
  components: { DeleteConfirmationDialog },
  data() {
    return {
      tips: [],
      isLoading: true,
      page: 1,
      count: 0,
      rowsPerPage: 10,
      paginationLength: 0,
      is_restored_tip: false,
      deleteDialog: false,
      itemToDelete: null,
      deleteLoading: false,
      bulkUploadDialog: false,
      uploadStage: "select", // 'select' | 'preview' | 'result'
      isDragging: false,
      bulkUploadFile: null,
      bulkUploadFileError: "",
      isPreviewing: false,
      isUploading: false,
      isDownloadingTemplate: false,
      isDownloadingErrors: false,
      previewData: null,
      resultData: null,
      previewHeaders: [
        { text: "Row", value: "row_number", width: "70px", sortable: false },
        { text: "Tip Date", value: "tip_date", width: "120px", sortable: false },
        { text: "Title", value: "title", sortable: false },
        { text: "Result", value: "result", width: "120px", sortable: false },
        { text: "Reason", value: "reason", sortable: false },
      ],
      headers: [
        { text: "Sr No", value: "sr_no", width: "80px", sortable: false },
        { text: "Status", value: "status", width: "100px", sortable: false },
        { text: "Tip Date", value: "tip_date", width: "150px", sortable: false },
        { text: "Title & Summary", value: "title", sortable: false },
        {
          text: "Created By",
          value: "created_by",
          width: "120px",
          sortable: false,
        },
        {
          text: "Created At",
          value: "created_at",
          width: "150px",
          sortable: false,
        },
        {
          text: "Actions",
          value: "actions",
          width: "100px",
          sortable: false,
          align: "end",
        },
      ],
      filter: {
        search_keyword: null,
        is_active: null,
        show_deleted: null,
      },
      statuses: [
        { id: 0, text: "Select Status", value: "" },
        { id: "ACTIVE", text: "ACTIVE", value: "1" },
        { id: "INACTIVE", text: "INACTIVE", value: "0" },
      ],
      deletedStatuses: [
        { text: "Active Daily Tips", value: false },
        { text: "Deleted Daily Tips", value: true },
      ],
    };
  },

  computed: {
    hasActiveFilters() {
      return (
        this.filter.search_keyword ||
        this.filter.is_active ||
        this.filter.show_deleted
      );
    },

  },

  mounted() {
    this.getData();
  },

  methods: {
    getActiveFiltersCount() {
      let count = 0;
      if (this.filter.search_keyword) count++;
      if (this.filter.is_active !== null && this.filter.is_active !== "")
        count++;
      if (
        this.filter.show_deleted !== null &&
        this.filter.show_deleted !== false
      )
        count++;
      return count;
    },

    clearAllFilters() {
      this.filter.search_keyword = null;
      this.filter.is_active = null;
      this.filter.show_deleted = null;
      this.page = 1;
      this.getData();
    },

    async getData() {
      this.isLoading = true;

      this.filter.search_keyword = this.filter.search_keyword || "";
      this.filter.is_active = this.filter.is_active || "";
      this.filter.show_deleted = this.filter.show_deleted || false;

      let query =
        "page=" +
        this.page +
        "&rowsPerPage=" +
        this.rowsPerPage +
        "&show_deleted=" +
        this.filter.show_deleted +
        "&is_active=" +
        this.filter.is_active +
        "&search_keyword=" +
        this.filter.search_keyword;

      await axios
        .get(`daily-tips?${query}`)
        .then((response) => {
          this.tips = response.data.data.data || response.data.data;
          this.count = response.data.count;
          this.paginationLength = Math.ceil(this.count / this.rowsPerPage);
        })
        .catch(function (error) {
          console.log(error);
          if (error.response && error.response.status === 401) {
            window.location.href = "/auth/login";
          }
        });
      this.isLoading = false;
    },

    openDeleteDialog(item) {
      this.itemToDelete = item;
      this.deleteDialog = true;
    },
    async confirmDelete() {
      this.deleteLoading = true;
      try {
        await axios.post(`/daily-tips/delete/${this.encryptIt(this.itemToDelete.id)}`);
        this.deleteDialog = false;
        this.itemToDelete = null;
        this.getData();
      } catch (error) {
        console.log(error);
      }
      this.deleteLoading = false;
    },

    async Restore_this(id) {
      await axios
        .post(`/daily-tips/restore/${this.encryptIt(id)}`)
        .then(() => {
          this.is_restored_tip = true;
          this.getData();
        })
        .catch(function (error) {
          console.log(error);
        });
    },

    isDeletePage() {
      return this.filter.show_deleted === true;
    },

    isToday(dateString) {
      if (!dateString) return false;
      const today = new Date();
      const date = new Date(dateString);
      return (
        date.getFullYear() === today.getFullYear() &&
        date.getMonth() === today.getMonth() &&
        date.getDate() === today.getDate()
      );
    },

    formatDate(dateString) {
      if (!dateString) return "";
      return new Date(dateString).toLocaleDateString();
    },

    formatDateTime(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      return date.toLocaleDateString() + " " + date.toLocaleTimeString();
    },

    getCreatedByName(createdBy) {
      if (!createdBy) return "N/A";
      const name = [createdBy.first_name, createdBy.last_name]
        .filter(Boolean)
        .join(" ");
      return name || createdBy.user_name || "N/A";
    },

    openBulkUploadDialog() {
      this.resetToSelectStage();
      this.bulkUploadDialog = true;
    },

    closeBulkUploadDialog() {
      this.bulkUploadDialog = false;
      if (this.uploadStage === "result") {
        this.getData();
      }
      this.resetToSelectStage();
    },

    resetToSelectStage() {
      this.uploadStage = "select";
      this.bulkUploadFile = null;
      this.bulkUploadFileError = "";
      this.previewData = null;
      this.resultData = null;
      if (this.$refs.fileInput) this.$refs.fileInput.value = "";
    },

    onFileDrop(event) {
      this.isDragging = false;
      const file = event.dataTransfer?.files?.[0];
      if (file) this.handleSelectedFile(file);
    },

    onFileSelected(event) {
      const file = event.target.files?.[0];
      if (file) this.handleSelectedFile(file);
    },

    handleSelectedFile(file) {
      const validExtensions = [".xlsx", ".xls"];
      const isValid = validExtensions.some((ext) =>
        file.name.toLowerCase().endsWith(ext)
      );
      if (!isValid) {
        this.bulkUploadFileError = "Please select a .xlsx or .xls file.";
        return;
      }

      this.bulkUploadFile = file;
      this.bulkUploadFileError = "";
      this.previewFile();
    },

    async previewFile() {
      this.isPreviewing = true;
      this.bulkUploadFileError = "";

      const formData = new FormData();
      formData.append("file", this.bulkUploadFile);

      try {
        const response = await axios.post("/daily-tips/upload/preview", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        this.previewData = response.data.data;
        this.uploadStage = "preview";
      } catch (error) {
        console.log(error);
        this.bulkUploadFileError =
          error.response?.data?.message || "Failed to validate file.";
        this.bulkUploadFile = null;
      }
      this.isPreviewing = false;
    },

    async confirmBulkUpload() {
      if (!this.bulkUploadFile) return;

      this.isUploading = true;

      const formData = new FormData();
      formData.append("file", this.bulkUploadFile);

      try {
        const response = await axios.post("/daily-tips/upload", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        this.resultData = response.data.data;
        this.uploadStage = "result";
      } catch (error) {
        console.log(error);
        this.$toast?.error?.(
          error.response?.data?.message || "Failed to upload file."
        );
      }
      this.isUploading = false;
    },

    async downloadTemplate() {
      this.isDownloadingTemplate = true;
      try {
        const response = await axios.get("/daily-tips/upload/template", {
          responseType: "blob",
        });
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "daily_tip_upload_template.xlsx");
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.log(error);
        this.$toast?.error?.("Failed to download template");
      }
      this.isDownloadingTemplate = false;
    },

    async downloadErrorReport() {
      if (!this.resultData?.rejected?.length) return;

      this.isDownloadingErrors = true;
      try {
        const response = await axios.post(
          "/daily-tips/upload/errors",
          { rows: this.resultData.rejected },
          { responseType: "blob" }
        );
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "daily_tip_upload_errors.xlsx");
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.log(error);
        this.$toast?.error?.("Failed to download error report");
      }
      this.isDownloadingErrors = false;
    },

    formatFileSize(bytes) {
      if (!bytes) return "0 KB";
      const kb = bytes / 1024;
      return kb < 1024 ? `${Math.round(kb)} KB` : `${(kb / 1024).toFixed(1)} MB`;
    },

    previewAlertType(summary, isResult = false) {
      const importCount = isResult ? summary.imported : summary.will_import;
      if (importCount === 0) return "error";
      if (summary.duplicate > 0 || summary.failed > 0) return "warning";
      return "success";
    },

    previewAlertText(summary) {
      if (summary.will_import === 0) {
        return `Nothing would import from ${summary.total_rows} rows`;
      }
      if (summary.duplicate > 0 || summary.failed > 0) {
        return `${summary.will_import} of ${summary.total_rows} rows will import — ${
          summary.duplicate + summary.failed
        } skipped`;
      }
      return `All ${summary.total_rows} rows will import successfully`;
    },

    resultAlertText(summary) {
      if (summary.imported === 0) {
        return `Nothing imported from ${summary.total_rows} rows`;
      }
      if (summary.duplicate > 0 || summary.failed > 0) {
        return `Imported ${summary.imported} of ${summary.total_rows} rows — ${
          summary.duplicate + summary.failed
        } skipped`;
      }
      return `All ${summary.total_rows} rows imported successfully`;
    },

    resultChipColor(result) {
      if (result === "will_import") return "success";
      if (result === "duplicate") return "warning darken-2";
      return "error";
    },

    resultLabel(result) {
      if (result === "will_import") return "Will Import";
      if (result === "duplicate") return "Duplicate";
      return "Failed";
    },
  },
};
</script>

<style scoped>
.v-data-table >>> .v-data-table__wrapper {
  border-radius: 0;
}

.v-data-table >>> thead th {
  background-color: #f8f9fa !important;
  border-bottom: 1px solid #dee2e6 !important;
  color: #495057 !important;
  font-weight: 600 !important;
  font-size: 0.75rem !important;
  padding: 12px 16px !important;
}

.v-data-table >>> tbody tr {
  border-bottom: 1px solid #f0f0f0;
}

.v-data-table >>> tbody tr:hover {
  background-color: #f8f9fa !important;
}

.v-data-table >>> tbody td {
  padding: 12px 16px !important;
  font-size: 0.875rem;
}

.empty-table {
  min-height: 400px;
}

.v-card-subtitle {
  border-bottom: 1px solid #f0f0f0;
}

.text-h6 {
  font-size: 1.1rem !important;
}

.text-caption {
  font-size: 0.75rem !important;
}

.action-btn.v-btn--icon {
  width: 36px !important;
  height: 36px !important;
}

.action-btn.v-btn--icon .v-icon {
  font-size: 20px !important;
}

.status-chip.v-chip {
  height: 28px !important;
  font-size: 0.875rem !important;
  padding: 0 12px !important;
}

.status-chip.v-chip .v-icon {
  font-size: 18px !important;
}

.v-pagination {
  justify-content: center;
}

.v-pagination >>> .v-pagination__item {
  min-width: 32px !important;
  height: 32px !important;
  font-size: 0.875rem !important;
}

.upload-dropzone {
  border: 2px dashed #c0c0c0;
  border-radius: 8px;
  padding: 40px 16px;
  text-align: center;
  cursor: pointer;
  transition: border-color 0.2s, background-color 0.2s;
}

.upload-dropzone:hover,
.upload-dropzone--active {
  border-color: var(--v-primary-base, #1976d2);
  background-color: rgba(25, 118, 210, 0.04);
}

.file-chip {
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  padding: 10px 14px;
}

.stat-tile {
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  padding: 8px 12px;
  text-align: center;
}

.preview-table >>> th,
.rejected-table >>> th {
  font-size: 0.7rem !important;
  text-transform: uppercase;
  color: #757575 !important;
}
</style>
